import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { Tag } from "components/DesignSystem/Tag/Tag";
import {
  AmendmentStatusLabel,
  EstimdatedTimeColumn,
} from "components/tasks/taskForForeignCA/ForeignCATask";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { FilledCircle } from "components/icons/FilledCircle";
import { SmallWarningInfo } from "components/icons/SmallWarningInfo";
import { BOOKS_GLOBAL_STATUS_COLORS } from "constants/booksGlobalStatus";
import { DONT_NEED, FILED_EXTERNALLY } from "constants/cartItemDeletionReasons";
import { STATUS_ON_HOLD } from "components/TaskTracker/Task1120FilterOptions";
import dayjs from "dayjs";
import { createColumnHelper } from "react-table-8.10.7";
import ArrowBend from "static/images/ArrowBendDownRight.svg";
import VerticalLine from "static/images/VerticalLine.svg";
import {
  TaskDeadlineCurrentState,
  TaskDeadlineOverviewItem,
} from "types/Models/task";
import { formatDate } from "utils/formatDate";
import { FilingTitleLink } from "./FilingTitleLink";
import { FilingActionCta } from "./FilingActionCta";
import { TaskDeadlineOptions } from "./TaskDeadlineOptions";
import { ClientStageTagTypes, TaskStates } from "./TaskStates";
import { JourneyTooltip } from "components/home/TaxComponents/JourneyTooltip";
import {
  buildClientProgressStepDetails,
  getClientStageLabel,
} from "utils/clientProgress";
import { QuestionWithCircle } from "components/icons/QuestionWithCircle";
import { Button } from "components/DesignSystem/Button/Button";
import { useModal } from "hooks/useModal";
import EditJurisdiction from "components/Entity/EntityFormEngine/EditJurisdiction";
import { PAYMENT, SUBMITTED } from "constants/taskStates";
import { getIsEtdHidden } from "utils/getIsEtdHidden";
import { getTaskStatesFromTaskCategory } from "utils/getTaskStatesFromTaskCategory";
import { Cart } from "components/icons/Cart";
import { SmallSparkle } from "components/icons/SmallSparkle";
import { SmallInfo } from "components/icons/SmallInfo";
import { DISPLAY_DATE } from "constants/date";

const InfoRequiredDeadline = ({
  entityUuid,
  alarmStateId,
}: {
  entityUuid: string;
  alarmStateId: string | null | undefined;
}) => {
  const editModal = useModal();

  const tooltipContent = (
    <div className="t-flex t-flex-col t-gap-2 t-text-left">
      <span>
        More information required to determined the exact due deadline.
      </span>
      <Button
        customType="secondary"
        size="xsmall"
        onPointerDown={editModal.open}
      >
        Fill information
      </Button>
    </div>
  );

  return (
    <>
      <span className="t-flex t-items-center t-gap-1 t-text-body-sm t-cursor-default">
        <ToolTip
          key={String(editModal.isOpen)}
          text={tooltipContent}
          propClass="t-max-w-[240px]"
          side="top"
        >
          <span className="t-flex t-items-center t-gap-1">
            <span className="t-text-text-30 t-text-body-italic t-italic sm:t-text-body-sm-italic">
              Info required
            </span>
            <QuestionWithCircle className="t-text-text-30" />
          </span>
        </ToolTip>
      </span>
      {alarmStateId && (
        <EditJurisdiction
          isOpen={editModal.isOpen}
          close={editModal.close}
          stateId={alarmStateId}
          entityId={entityUuid}
          modal
        />
      )}
    </>
  );
};

const createColumn = createColumnHelper<
  TaskDeadlineOverviewItem & {
    subRows?: TaskDeadlineOverviewItem[];
    isSubTask?: boolean;
    isLast?: boolean;
  }
>();

export const getIsTaskCompleted = (item: TaskDeadlineOverviewItem) => {
  const lastStep = item.task_states?.[item.task_states?.length - 1];
  const isTaskCompleted =
    lastStep?.is_task_state_completed ||
    (item.current_state?.type === lastStep?.type_str && lastStep?.is_final);
  return isTaskCompleted;
};

export const getEntityTaskTableColumns = (isCpa: boolean) => [
  createColumn.accessor("task_title", {
    header: "FILING",
    size: 35,
    cell: (info) => {
      const { row } = info;
      const value = info.getValue();

      const {
        original: {
          task_uuid,
          alarm_uuid,
          isLast,
          isSubTask,
          display_text,
          cart_status,
          source,
        },
      } = row;

      const hasSingleSubtask = row.id.split(".")[1] === "0";
      const showArchiveIcon =
        row.original.delete_reason || row.original.is_archived;

      let isAddedToCart = source === "CART" && cart_status === "IN_CART";

      if (!task_uuid) {
        const titleIcons = (
          <>
            {display_text && (
              <ToolTip text={display_text}>
                <span className="t-w-4">
                  <QuestionWithCircle className="t-text-text-60" />
                </span>
              </ToolTip>
            )}
            {showArchiveIcon && (
              <ToolTip text="Archived">
                <span className="t-w-4">
                  <Tag tagType="gray" icon={false} size="small">
                    A
                  </Tag>
                </span>
              </ToolTip>
            )}
            {!row.original.delete_reason && row.original.is_action_pending && (
              <ToolTip text="Action pending on you">
                <span className="t-w-4">
                  <FilledCircle className="t-text-red-50" />
                </span>
              </ToolTip>
            )}
          </>
        );

        return (
          <div
            className="t-relative t-flex t-gap-1"
            style={{
              paddingLeft: `${row.depth}rem`,
            }}
          >
            <span>
              {row.depth > 0 ? (
                <>
                  <img src={ArrowBend} alt="Subtask" />
                  <img
                    className={classNames(
                      "t-absolute t-left-[16.8px] sm:t-hidden",
                      {
                        "t-top-[-64px]": isLast,
                        "-t-top-[15px]": !isLast,
                        "t-hidden": row?.original?.isLast && hasSingleSubtask,
                      }
                    )}
                    src={VerticalLine}
                    alt="Subtask"
                  />
                </>
              ) : null}
            </span>

            <div className="t-flex t-gap-1.5 t-items-start">
              <div className="t-flex t-flex-col t-line-clamp-1 sm:t-line-clamp-none">
                <div className="t-text-body-sm t-text-text-60 t-line-clamp-1 sm:t-hidden">
                  {value}
                </div>
                <div className="t-hidden t-text-body-sm t-text-text-60 sm:t-flex sm:t-items-start sm:t-gap-1.5">
                  <span>{value}</span>
                  {titleIcons}
                </div>
                {!isAddedToCart && (
                  <ToolTip
                    text="This filing was suggested based on the information you provided about your company. Please review it before launching. Contact the team if you need assistance."
                    side="top"
                    collisionPadding={16}
                    propClass="t-w-72 !t-text-left"
                  >
                    <span className="t-flex t-gap-0.5 t-items-center t-w-fit">
                      <SmallSparkle className="t-text-neutral" />
                      <span className="t-text-caption t-italic t-text-neutral-50">
                        Suggested by system
                      </span>
                      <SmallInfo className="t-text-neutral-40 t-size-3" />
                    </span>
                  </ToolTip>
                )}
                {isAddedToCart && (
                  <div className="t-flex t-items-center t-gap-1">
                    <Cart className="t-text-neutral-50 t-size-3" />
                    <span className="t-text-neutral-50 t-text-caption t-italic">
                      Added to cart
                    </span>
                  </div>
                )}
              </div>

              <div className="t-contents sm:t-hidden">{titleIcons}</div>
            </div>
          </div>
        );
      }

      return (
        <div
          className="t-relative t-flex t-gap-1"
          style={{
            paddingLeft: `${row.depth}rem`,
          }}
        >
          <span>
            {row.depth > 0 ? (
              <>
                <img src={ArrowBend} alt="Subtask" />
                <img
                  className={classNames(
                    "t-absolute t-left-[16.8px] sm:t-hidden",
                    {
                      "t-top-[-64px]": isLast,
                      "-t-top-[15px]": !isLast,
                      "t-hidden": row?.original?.isLast && hasSingleSubtask,
                    }
                  )}
                  src={VerticalLine}
                  alt="Subtask"
                />
              </>
            ) : null}
          </span>

          <div className="t-flex t-items-center t-gap-2 t-pr-3 t-text-body-sm t-font-medium t-text-text-100 t-group">
            <ToolTip text={value}>
              <div className="t-line-clamp-1 sm:t-line-clamp-none">
                <FilingTitleLink taskUuid={task_uuid} title={value} />
              </div>
            </ToolTip>
            {display_text && (
              <ToolTip text={display_text}>
                <span>
                  <QuestionWithCircle className="t-text-purple" />
                </span>
              </ToolTip>
            )}
          </div>
        </div>
      );
    },
  }),
  createColumn.accessor("deadline_date", {
    header: "DEADLINE",
    size: 17,
    cell: (info) => {
      const item = info.row.original;
      const isActionPendingOnUser = item.is_action_pending;
      const deadlineCrossed = dayjs(info.getValue()).isBefore(dayjs(), "day");
      const isTaskCompleted = getIsTaskCompleted(item);
      const isArchived = item.is_archived;

      const showWarning =
        !isTaskCompleted &&
        deadlineCrossed &&
        isActionPendingOnUser &&
        !isArchived;

      const deadlineDate = info.getValue();

      if (!deadlineDate) {
        const isAlarmAnnualReport =
          item.source === "ALARMS" &&
          (item.base_task_template.title === "State Annual Report" ||
            item.task_title === "California Statement of Information");

        if (isAlarmAnnualReport) {
          return (
            <InfoRequiredDeadline
              entityUuid={item.entity.uuid}
              alarmStateId={item.alarm_state_id}
            />
          );
        }

        return <div className="t-text-body-sm t-text-text-60">-</div>;
      }

      return (
        <div
          className={classNames("t-flex t-gap-1.5 t-items-center", {
            "t-text-red": showWarning,
            "t-text-text-60": !showWarning,
          })}
        >
          <CalendarIcon />
          <div className="t-text-body-sm">{formatDate(deadlineDate)}</div>
          {showWarning && <SmallWarningInfo className="t-text-red" />}
        </div>
      );
    },
  }),
  ...(isCpa
    ? [
        createColumn.accessor("estimated_time_of_delivery", {
          header: () => (
            <ToolTip text="Estimated time of delivery">
              <span className="t-pl-1">ETD</span>
            </ToolTip>
          ),
          size: 5,
          cell: (info) => {
            const { task_uuid, estimated_time_of_delivery } = info.row.original;
            if (!task_uuid) return <span>-</span>;
            return (
              <EstimdatedTimeColumn
                taskData={
                  { uuid: task_uuid, estimated_time_of_delivery } as any
                }
                isCustomTrigger
              />
            );
          },
        }),
      ]
    : []),
  createColumn.accessor("task_states", {
    header: "Status",
    size: 23,
    cell: (info) => {
      const item = info.row.original;
      const unlaunchedTaskStates = getTaskStatesFromTaskCategory(
        item.task_category_type
      );

      const taskStates =
        Array.isArray(info.getValue()) && info.getValue().length > 0
          ? info.getValue()
          : unlaunchedTaskStates;

      const name = item.archived_by?.full_name || null;

      const currentState = item.current_state as TaskDeadlineCurrentState;
      const etdDate = item.latest_etd || null;
      const isEtdHidden =
        getIsEtdHidden(
          currentState?.key || item.client_progress?.current_key
        ) || currentState?.type === SUBMITTED;

      const getStatusContent = () => {
        if (item.delete_reason === DONT_NEED) {
          return (
            <ToolTip
              text={
                <div className="t-text-body-sm">
                  <span className="t-italic">
                    This filing has been marked as ‘Not required’
                  </span>
                  {name && (
                    <span className="t-ml-1">
                      <span className="t-italic">by</span> {name}
                    </span>
                  )}
                </div>
              }
            >
              <span>
                <Tag tagType="red" icon={false} rounded transparent>
                  Not required
                </Tag>
              </span>
            </ToolTip>
          );
        }

        if (item.delete_reason === FILED_EXTERNALLY) {
          return (
            <ToolTip
              text={
                <div className="t-text-body-sm">
                  <span className="t-italic">
                    This filing has been marked as ‘Filed externally’
                  </span>
                  {name && (
                    <span className="t-ml-1">
                      <span className="t-italic">by</span> {name}
                    </span>
                  )}
                </div>
              }
            >
              <span>
                <Tag tagType="green" icon={false} rounded transparent>
                  Filed externally
                </Tag>
              </span>
            </ToolTip>
          );
        }

        if (item.is_archived) {
          return (
            <Tag tagType="gray" icon={false} rounded transparent>
              Archived
            </Tag>
          );
        }

        if (item.books_global_status) {
          return (
            <span>
              <Tag
                size="small"
                rounded
                transparent
                tagType={
                  BOOKS_GLOBAL_STATUS_COLORS[item.books_global_status.key] ||
                  "blue"
                }
              >
                {item.books_global_status.name}
              </Tag>
            </span>
          );
        }

        if (item.client_progress) {
          const isOnHold = item.client_progress.is_on_hold;
          const key = isOnHold
            ? STATUS_ON_HOLD
            : currentState?.key || item.client_progress.current_key;
          const label = isOnHold
            ? "On Hold"
            : getClientStageLabel({
                key: item.client_progress.current_key,
                label: item.client_progress.current_label,
                paymentStatus: item.payment_status,
              });
          const stepDetails = buildClientProgressStepDetails(
            item.client_progress,
            item.payment_status
          );
          return (
            <JourneyTooltip
              stepDetails={stepDetails}
              latestEtd={item.latest_etd}
              taskUuid={item.task_uuid}
              previousCycles={item.client_progress.previous_cycles}
            >
              <span className="t-flex t-gap-2">
                <Tag
                  size="small"
                  rounded
                  tagType={ClientStageTagTypes[key] || "blue"}
                  transparent
                >
                  {label}
                </Tag>
                {Boolean(item.client_progress.amendment_number) && (
                  <span>
                    (Amendment {item.client_progress.amendment_number})
                  </span>
                )}
              </span>
            </JourneyTooltip>
          );
        }

        if (currentState?.key) {
          return (
            <span>
              <Tag
                size="small"
                rounded
                tagType={ClientStageTagTypes[currentState.key] || "blue"}
                transparent
              >
                {currentState.name}
              </Tag>
            </span>
          );
        }

        return (
          <TaskStates
            states={taskStates}
            currentStateType={info.row.original.current_state.type || PAYMENT}
          />
        );
      };

      return (
        <div className="t-flex t-flex-col t-gap-0.5">
          {getStatusContent()}
          {etdDate && !isEtdHidden && (
            <span className="t-text-body-sm t-text-text-30">
              Draft expected: {dayjs(etdDate).format(DISPLAY_DATE)}
            </span>
          )}
        </div>
      );
    },
  }),
  createColumn.accessor("alarm_uuid", {
    header: "ACTION",
    size: 15,
    cell: (info) => {
      const item = info.row.original;
      const meta = info.table.options.meta as {
        openActions: (taskId: string) => void;
      };

      return (
        <FilingActionCta
          item={item}
          isTaskCompleted={getIsTaskCompleted(item)}
          onOpenActions={meta.openActions}
        />
      );
    },
  }),
  createColumn.accessor("task_uuid", {
    header: "",
    size: 5,
    cell: (info) => {
      const meta = info.table.options.meta as {
        openSliderChat: () => void;
      };
      return (
        <TaskDeadlineOptions
          taskDeadline={info.row.original}
          openSliderChat={meta.openSliderChat}
        />
      );
    },
  }),
];
